import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import Swal, {
  SweetAlertIcon,
  SweetAlertPosition,
  SweetAlertOptions
} from 'sweetalert2';

import { TranslateService } from '@ngx-translate/core';

import { CookieService } from './cookie.service';

import { ResponseCode } from '../enums/responsecode.enum';
import { Route } from '../enums/route.enum';
import { Name } from '../enums/name.enum';

export interface DialogOptions {
  icon?: SweetAlertIcon;

  title?: string;
  text?: string;
  html?: string;

  confirmButtonText?: string;
  confirmButtonColor?: string;

  cancelButtonText?: string;
  cancelButtonColor?: string;

  footer?: string;

  allowOutsideClick?: boolean;
  allowEscapeKey?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AlertService {

  // =========================================
  // DEPENDENCY INJECTION (Angular 21)
  // =========================================

  private readonly translate = inject(TranslateService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);

  // =========================================
  // DEFAULT CONFIG
  // =========================================

  private readonly DEFAULT_TIMER = 3000;

  private readonly DEFAULT_CONFIRM_COLOR = '#0c1e43';

  private readonly DEFAULT_CANCEL_COLOR = 'rgba(0,0,0,0.5)';

  // =========================================
  // SUCCESS
  // =========================================

  success(
    title = 'Successfully done!',
    icon: SweetAlertIcon = 'success',
    position: SweetAlertPosition = 'top-end',
    timer = this.DEFAULT_TIMER
  ): void {
    this.toast(title, icon, position, timer);
  }

  // =========================================
  // ERROR
  // =========================================

  error(
    title = 'Something went wrong!',
    icon: SweetAlertIcon = 'error',
    position: SweetAlertPosition = 'top-end',
    timer = this.DEFAULT_TIMER
  ): void {
    this.toast(title, icon, position, timer);
  }

  // =========================================
  // WARNING
  // =========================================

  warning(
    title = 'Warning!',
    icon: SweetAlertIcon = 'warning',
    position: SweetAlertPosition = 'top-end',
    timer = this.DEFAULT_TIMER
  ): void {
    this.toast(title, icon, position, timer);
  }

  // =========================================
  // INFO
  // =========================================

  information(
    title = 'Information!',
    icon: SweetAlertIcon = 'info',
    position: SweetAlertPosition = 'top-end',
    timer = this.DEFAULT_TIMER
  ): void {
    this.toast(title, icon, position, timer);
  }

  // =========================================
  // TOAST
  // =========================================

  private toast(
    title: string,
    icon: SweetAlertIcon,
    position: SweetAlertPosition,
    timer: number
  ): void {

    const toast = Swal.mixin({
      toast: true,
      position,
      showConfirmButton: false,
      timer,
      timerProgressBar: true,

      didOpen: (element) => {
        element.addEventListener('mouseenter', Swal.stopTimer);
        element.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });

    toast.fire({
      icon,
      title
    });
  }

  // =========================================
  // DEFAULT SUCCESS HANDLER
  // =========================================

  defaultMessageHandler(
    code: string,
    data: unknown = null
  ): void {

    const key = this.getResponseCode(code);

    const message = data
      ? this.translate.instant(`responsecode.${key}`, data)
      : this.translate.instant(`responsecode.${key}`);

    this.success(message);
  }

  // =========================================
  // DEFAULT ERROR HANDLER
  // =========================================

  defaultErrorMessageHandler(
    err: any,
    data: unknown = null
  ): void {

    // -----------------------------------------
    // Unauthorized
    // -----------------------------------------

    if (err?.status === 401) {

      this.cookieService.delete(Name.AUTH_TOKEN);

      localStorage.clear();
      sessionStorage.clear();

      this.router.navigateByUrl(
        `/${Route.AUTH}/${Route.SIGNIN}?signout=true`
      );

      return;
    }

    // -----------------------------------------
    // Error Message
    // -----------------------------------------

    const code = err?.error?.code;

    const key = this.getResponseCode(code);

    const message = data
      ? this.translate.instant(`responsecode.${key}`, data)
      : this.translate.instant(`responsecode.${key}`);

    this.error(message);
  }

  // =========================================
  // MODAL
  // =========================================

  async dialogModal(
    options: DialogOptions = {}
  ): Promise<boolean> {

    const swalOptions: SweetAlertOptions = {

      icon: options.icon,

      title: options.title,

      text: options.text,

      html: options.html,

      footer: options.footer,

      allowOutsideClick: options.allowOutsideClick ?? false,

      allowEscapeKey: options.allowEscapeKey ?? true,

      customClass: {
        confirmButton: 'sw-btn',
        cancelButton: 'sw-btn',
        title: 'sw-title',
        popup: 'sw-has-close'
      },

      showConfirmButton: !!options.confirmButtonText,

      confirmButtonText: options.confirmButtonText,

      confirmButtonColor:
        options.confirmButtonColor ||
        this.DEFAULT_CONFIRM_COLOR,

      showCancelButton: !!options.cancelButtonText,

      cancelButtonText: options.cancelButtonText,

      cancelButtonColor:
        options.cancelButtonColor ||
        this.DEFAULT_CANCEL_COLOR,

      didOpen: () => {
        this.appendCloseButton();
      }
    };

    const result = await Swal.fire(swalOptions);

    return result.isConfirmed === true;
  }

  // =========================================
  // HELPERS
  // =========================================

  private getResponseCode(code: string): string {

    if (ResponseCode[code as keyof typeof ResponseCode]) {
      return ResponseCode[code as keyof typeof ResponseCode];
    }

    return ResponseCode.SystemInternalError;
  }

  private appendCloseButton(): void {

    const popup = document.querySelector(
      '.sw-has-close'
    ) as HTMLElement | null;

    if (!popup) {
      return;
    }

    const button = document.createElement('button');

    button.innerHTML = '&times;';

    button.classList.add('sw-close-btn');

    button.onclick = () => Swal.close();

    popup.appendChild(button);
  }
}